import { useEffect, useState } from "react";
import { useEvent } from "@/context/event-context";
import { formatEventDate, formatEventTime } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { useCountdown } from "@/hooks/use-countdown";

const NumberBadge = ({ n }: { n: number }) => (
  <span className="inline-flex items-center gap-2">
    <span className="w-[18px] h-[18px] rounded-full bg-ink text-white text-[10px] font-heading font-bold flex items-center justify-center shrink-0">
      {n}
    </span>
    <span className="w-[15px] h-px bg-ink/60" />
  </span>
);

const HeroSkeleton = () => (
  <section className="relative min-h-screen flex flex-col justify-end px-[4vw] pb-[4vw] overflow-hidden bg-cream">
    <div className="relative z-10 max-w-[900px] mb-16">
      <Skeleton className="h-4 w-48 mb-4" />
      <Skeleton className="h-20 w-[600px] max-w-full mb-4" />
      <Skeleton className="h-20 w-[400px] max-w-full mb-12" />
      <Skeleton className="h-14 w-48" />
    </div>
    <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16 border-t border-ink/15 pt-8 max-w-[900px]">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-40" />
        </div>
      ))}
    </div>
  </section>
);

// Skip the video entirely on small viewports — at opacity-20 it's barely visible
// there anyway, and the poster image alone fires LCP in <2s instead of ~5s.
function useEnableHeroVideo() {
  const [enabled, setEnabled] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 640px)");
    const update = () => setEnabled(mq.matches);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return enabled;
}

const Hero = () => {
  const { event, isLoading } = useEvent();
  const enableVideo = useEnableHeroVideo();
  const countdown = useCountdown(event?.date);

  if (isLoading) return <HeroSkeleton />;
  if (!event) return null;

  const eventIsLive = countdown.days === 0 && countdown.hours === 0 && countdown.mins === 0;

  const videoUrl = event.theme_config?.heroVideoUrl || "/video/hero.mp4";
  const posterUrl = event.theme_config?.heroPosterUrl || "/images/hero-poster.jpg";
  const dateTimeValue = `${formatEventDate(event.date)}\n${formatEventTime(event.date, event.end_date)}`;
  const locationValue = `${event.venue_name}\n${event.venue_address}`;
  const dressCodeValue = event.dress_code || "Strictly White.\nSophisticated Attire.";

  const metaItems = [
    { label: "Date & Time", value: dateTimeValue },
    { label: "Location", value: locationValue },
    { label: "Dress Code", value: dressCodeValue },
  ];

  // Split event name for display (e.g., "The All White Experience" → "The All White" + "Experience")
  const words = event.name.split(" ");
  const lastWord = words.pop();
  const firstLine = words.join(" ");

  return (
    <section data-nav-theme="dark" className="relative min-h-screen flex flex-col justify-end px-4 sm:px-[4vw] pt-24 sm:pt-28 lg:pt-32 pb-8 sm:pb-[4vw] overflow-hidden bg-cream">
      {/* Video — native ratio, right-aligned, hidden on small mobile for performance */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute right-[5vw] top-1/2 -translate-y-1/2 h-[60vh] sm:h-[80vh] lg:h-[95vh] opacity-20 sm:opacity-40"
          style={{
            aspectRatio: "320 / 568",
            WebkitMaskImage: "radial-gradient(ellipse 70% 70% at center, black 30%, transparent 100%)",
            maskImage: "radial-gradient(ellipse 70% 70% at center, black 30%, transparent 100%)",
          }}
        >
          {enableVideo ? (
            <video
              src={videoUrl}
              poster={posterUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={posterUrl}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
            />
          )}
        </div>
        {/* Softer gradient — warm cream instead of pure white */}
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/75 to-cream/20" />
        {/* Bottom vignette */}
        <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-cream/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-ink max-w-[900px] mb-8 sm:mb-16">
        <p
          className="font-heading uppercase text-ink/50 mb-3 sm:mb-4"
          style={{
            fontSize: "var(--label-size, 0.85rem)",
            fontWeight: "var(--label-weight, 800)",
            letterSpacing: "var(--label-tracking, 0.2em)",
          }}
        >
          Presented by For The City Ent.
        </p>

        <h1
          className="font-display text-ink"
          style={{
            fontSize: "var(--hero-size, clamp(2.25rem, 8vw, 7rem))",
            fontWeight: "var(--hero-weight, 400)",
            letterSpacing: "var(--hero-tracking, 0.05em)",
            lineHeight: "var(--hero-line-height, 1.1)",
            textTransform: "var(--hero-transform, uppercase)" as any,
            fontStyle: "var(--hero-style, normal)" as any,
          }}
        >
          {firstLine}
          <br />
          <span className="block">{lastWord}</span>
        </h1>

        <div className="mt-6 sm:mt-12 flex gap-3 sm:gap-6 items-center flex-wrap">
          <a
            href="#tickets"
            className="bg-ink text-white hover:bg-gold hover:text-white transition-colors active:scale-[0.97]"
            style={{
              fontSize: "var(--cta-size, 0.75rem)",
              fontWeight: "var(--cta-weight, 600)",
              letterSpacing: "var(--cta-tracking, 0.1em)",
              padding: "var(--cta-padding, 1.25rem 2.5rem)",
              borderRadius: "var(--cta-radius, 0)",
              textTransform: "uppercase" as any,
              fontFamily: "var(--font-sans)",
            }}
          >
            Secure Your Pass
          </a>
          <span
            className="font-sans uppercase text-ink/50"
            style={{
              fontSize: "var(--label-size, 0.85rem)",
              letterSpacing: "var(--label-tracking, 0.2em)",
              fontWeight: "var(--label-weight, 500)",
            }}
          >
            {new Date(event.date).toLocaleDateString("en-US", { month: "long", day: "numeric" })} — {event.venue_address}
          </span>
        </div>
      </div>

      {/* Countdown */}
      {!eventIsLive && (
        <div className="relative z-10 flex items-center gap-6 sm:gap-10 mb-8 sm:mb-12 max-w-[900px]">
          {[
            { value: countdown.days, label: "Days" },
            { value: countdown.hours, label: "Hours" },
            { value: countdown.mins, label: "Mins" },
          ].map((unit) => (
            <div key={unit.label} className="flex flex-col items-center">
              <span
                className="font-heading text-ink"
                style={{
                  fontSize: "var(--countdown-size, clamp(2rem, 5vw, 3.5rem))",
                  fontWeight: "var(--countdown-weight, 800)",
                  letterSpacing: "0.05em",
                }}
              >
                {String(unit.value).padStart(2, "0")}
              </span>
              <span
                className="font-heading uppercase text-ink/50 mt-1"
                style={{
                  fontSize: "var(--label-size, 0.6rem)",
                  letterSpacing: "var(--label-tracking, 0.2em)",
                  fontWeight: "var(--label-weight, 500)",
                }}
              >
                {unit.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Meta Grid */}
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16 border-t border-ink/15 pt-8 max-w-[900px]">
        {metaItems.map((item, i) => (
          <div key={item.label} className="flex flex-col gap-2 border-b border-ink/10 pb-4 md:border-b-0 md:pb-0">
            <div className="flex items-center gap-2">
              <NumberBadge n={i + 1} />
              <span
                className="font-heading uppercase text-ink/70"
                style={{
                  fontSize: "var(--label-size, 0.85rem)",
                  fontWeight: "var(--label-weight, 800)",
                  letterSpacing: "var(--label-tracking, 0.2em)",
                }}
              >
                {item.label}
              </span>
            </div>
            <p
              className="font-serif-elegant text-ink italic pl-[45px] whitespace-pre-line"
              style={{
                fontSize: "var(--body-size, 1.35rem)",
                fontWeight: "var(--body-weight, 400)",
                lineHeight: "var(--body-line-height, 1.7)",
              }}
            >
              {item.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Hero;
